import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Eye, EyeOff, ArrowRight, GraduationCap, BookOpen, Globe, CheckCircle } from 'lucide-react';

const IMAGES = ['/hu-login-1.png', '/feature-image-1.jpeg', '/login-page.jpeg'];

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [activeImg, setActiveImg] = useState(() => Math.floor(Math.random() * IMAGES.length));
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();

  const roleRoutes = {
    Admin: '/admin/dashboard', SuperAdmin: '/admin/dashboard',
    Teacher: '/teacher/dashboard', Lecturer: '/lecturer/dashboard', Student: '/student/home',
    Parent: '/programme-manager/home', ProgrammeManager: '/programme-manager/home', FinanceOfficer: '/admin/finance',
    HR_Officer: '/admin/hr', Registrar: '/admin/dashboard',
    CEO: '/admin/dashboard', Founder: '/admin/dashboard',
    Principal: '/admin/dashboard', HeadTeacher: '/admin/dashboard',
  };

  React.useEffect(() => {
    if (user) navigate(roleRoutes[user.role] || '/student/home');
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const id = setInterval(() => setActiveImg(i => (i + 1) % IMAGES.length), 8000);
    return () => clearInterval(id);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const userData = await login(email, password);
      const next = new URLSearchParams(window.location.search).get('next');
      navigate(next || roleRoutes[userData.role] || '/student/home');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">

      {/* ── Left panel ── */}
      <div className="hidden lg:flex lg:w-[52%] relative overflow-hidden" style={{ background: 'linear-gradient(145deg, #0D47A1 0%, #1565C0 50%, #1976D2 100%)' }}>

        {/* Decorative blobs */}
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, #42A5F5, transparent)' }} />
        <div className="absolute -bottom-32 -right-16 w-[500px] h-[500px] rounded-full opacity-15" style={{ background: 'radial-gradient(circle, #42A5F5, transparent)' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-5" style={{ background: 'radial-gradient(circle, white, transparent)' }} />

        {/* Curved right edge */}
        <svg className="absolute right-0 top-0 h-full" viewBox="0 0 60 800" preserveAspectRatio="none" fill="none">
          <path d="M60 0 C20 200, 20 600, 60 800 L60 0Z" fill="#EFF6FF" />
        </svg>

        {/* Hero image slideshow */}
        <div className="absolute inset-0" style={{ clipPath: 'inset(0 40px 0 0)' }}>
          {IMAGES.map((src, idx) => (
            <img
              key={src}
              src={src}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{
                opacity: idx === activeImg ? 0.22 : 0,
                transition: 'opacity 3000ms cubic-bezier(0.4, 0, 0.2, 1)',
                zIndex: idx === activeImg ? 2 : 1,
                willChange: 'opacity',
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full pr-16">
          {/* Logo — nudged left so it visually aligns with the heading below */}
          <img src="/main-logo-horinzontal.png" alt="HomeUni" style={{ width: '100%', maxWidth: '340px', height: 'auto', marginLeft: '-6px' }} className="object-contain self-start" />

          {/* Main text */}
          <div className="max-w-md">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#42A5F5]" />
              <span className="text-xs font-medium text-blue-100 tracking-wide">Home University · Online Learning</span>
            </div>
            <h1 className="text-[2.6rem] font-bold leading-[1.15] mb-5">
              Education<br />Wherever You<br />Feel at Home
            </h1>
            <p className="text-blue-100 text-[15px] leading-relaxed mb-10 max-w-sm">
              Enrol in programmes, earn certificates, and stay connected with your academic community — from anywhere in the world.
            </p>

            {/* Feature list */}
            <div className="space-y-3">
              {[
                'Access 50+ accredited programmes',
                'Learn at your own pace, fully online',
                'Earn verified digital certificates',
              ].map(f => (
                <div key={f} className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-[#42A5F5] flex-shrink-0" />
                  <span className="text-sm text-blue-100">{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stats row */}
          <div className="flex gap-3">
            {[
              { icon: GraduationCap, label: 'Students', value: '500+' },
              { icon: BookOpen, label: 'Programmes', value: '50+' },
              { icon: Globe, label: 'Mode', value: '100% Online' },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 flex items-center gap-2.5 flex-1">
                <Icon className="w-4 h-4 text-[#42A5F5] flex-shrink-0" />
                <div>
                  <p className="text-[10px] text-blue-200 font-medium uppercase tracking-wide">{label}</p>
                  <p className="text-sm font-bold leading-tight">{value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-12 bg-[#EFF6FF] relative overflow-hidden">

        {/* Subtle background circles */}
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-[0.06]" style={{ background: 'radial-gradient(circle, #1565C0, transparent)', transform: 'translate(30%, -30%)' }} />
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full opacity-[0.05]" style={{ background: 'radial-gradient(circle, #1976D2, transparent)', transform: 'translate(-30%, 30%)' }} />

        <div className="w-full max-w-[420px] relative z-10">

          {/* Mobile logo — centred */}
          <div className="lg:hidden mb-8 flex justify-center">
            <img src="/main-logo-horinzontal.png" alt="HomeUni" style={{ width: '200px', height: 'auto' }} className="object-contain mx-auto" />
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl shadow-[0_8px_40px_rgba(21,101,192,0.1)] border border-[#BBDEFB]/80 p-8">

            {/* Header */}
            <div className="mb-7">
              <h3 className="text-[1.6rem] font-bold text-[#1B2A5E] mb-1 leading-tight">Welcome back</h3>
              <p className="text-slate-400 text-sm">Sign in to continue to your account</p>
            </div>

            {error && (
              <div className="mb-5 p-3 bg-red-50 border border-red-100 text-red-700 rounded-lg text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-[#1B2A5E] text-[13px] font-semibold">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@homeuni.edu.gh"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 border-[#BBDEFB] focus:border-[#1565C0] rounded-lg text-sm"
                  required
                  autoComplete="email"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-[#1B2A5E] text-[13px] font-semibold">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 pr-10 border-[#BBDEFB] focus:border-[#1565C0] rounded-lg text-sm"
                    required
                    autoComplete="current-password"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#1B2A5E] transition-colors"
                    data-testid="toggle-password-btn"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 rounded-lg font-semibold text-sm tracking-wide btn-navy shadow-[0_4px_16px_rgba(27,42,94,0.30)] hover:shadow-[0_6px_20px_rgba(27,42,94,0.40)] transition-all"
                disabled={isLoading}
                data-testid="login-submit-btn"
              >
                {isLoading ? 'Signing in...' : (
                  <span className="flex items-center justify-center gap-2">Sign in <ArrowRight className="w-4 h-4" /></span>
                )}
              </Button>
            </form>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-[#E2EEFF]" />
              <span className="text-xs text-slate-400 font-medium">or continue with</span>
              <div className="flex-1 h-px bg-[#E2EEFF]" />
            </div>

            <Button
              variant="outline"
              className="w-full h-11 rounded-lg border-[#BBDEFB] hover:bg-[#EFF6FF] hover:border-[#1B2A5E] transition-colors text-sm font-medium"
              onClick={loginWithGoogle}
              data-testid="google-login-btn"
            >
              <svg className="w-4 h-4 mr-2.5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Google
            </Button>
          </div>

          <p className="text-center text-[11px] text-slate-400 mt-5">
            © 2026 Home University. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
